/**
 * Conversation hooks for React Query
 *
 * Provides data fetching hooks for conversation management.
 * Uses the real Conversation Manager API via BFF.
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  listConversations,
  getConversation,
  getTranscript,
} from '@/api/conversations';
import type { ConversationFilters } from '@/types';

export function useConversations(
  filters: ConversationFilters = {},
  pageSize = 20
) {
  return useInfiniteQuery({
    queryKey: ['conversations', filters],
    queryFn: async ({ pageParam }) => {
      return listConversations(filters, {
        pageSize,
        pageToken: pageParam as string | undefined,
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_page_token,
    initialPageParam: undefined as string | undefined,
    staleTime: 30000,
  });
}

export function useConversation(
  conversationId: string | null,
  includeTranscript = true
) {
  return useQuery({
    queryKey: ['conversation', conversationId, includeTranscript],
    queryFn: async () => {
      if (!conversationId) {
        throw new Error('Conversation ID is required');
      }
      return getConversation(conversationId, includeTranscript);
    },
    enabled: !!conversationId,
    staleTime: 60000,
  });
}

export function useTranscript(conversationId: string | null) {
  return useQuery({
    queryKey: ['transcript', conversationId],
    queryFn: async () => {
      if (!conversationId) {
        throw new Error('Conversation ID is required');
      }
      return getTranscript(conversationId);
    },
    enabled: !!conversationId,
    staleTime: 300000, // Transcripts don't change often
  });
}
